import { Injectable, InjectionToken, inject } from '@angular/core';

import { Platform } from '@ionic/angular';
import { Storage } from '@ionic/storage-angular';

export const LOCAL_STORAGE_NATIVE_DRIVER = new InjectionToken<
  Parameters<Storage['defineDriver']>[0]
>('LOCAL_STORAGE_NATIVE_DRIVER');

@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  private platform = inject(Platform);
  private storage = inject(Storage);
  private nativeStorageDriver =
    inject(LOCAL_STORAGE_NATIVE_DRIVER, { optional: true }) ?? null;

  private mStorage: Storage | null = null;
  private cache = new Map<string, Promise<any>>();

  async init(): Promise<void> {
    if (this.mStorage) {
      return;
    }

    if (this.platform.is('capacitor') && this.nativeStorageDriver) {
      await this.storage.defineDriver(this.nativeStorageDriver);
    }

    this.mStorage = await this.storage.create();
  }

  async get(key: string): Promise<any> {
    if (this.cache.has(key)) {
      return this.cache.get(key);
    }

    const promise = this.init().then(() => this.mStorage?.get(key));
    this.cache.set(key, promise);

    const value = await promise;

    if (value === null || value === undefined) {
      this.cache.delete(key);
    }

    return value;
  }

  async set(key: string, value: any): Promise<any> {
    this.cache.set(key, Promise.resolve(value));
    await this.init();
    return this.mStorage?.set(key, value);
  }

  async remove(key: string): Promise<any> {
    this.cache.delete(key);
    await this.init();
    return this.mStorage?.remove(key);
  }

  async keys(): Promise<any> {
    await this.init();
    return this.mStorage?.keys();
  }
}
